import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuDTO } from 'src/app/shared/model/dto/MenuDTO';
import { Menu } from 'src/app/shared/model/Menu';
import { MenuProduct } from 'src/app/shared/model/MenuProduct';
import { RestaurantService } from 'src/app/core/services/restaurant.service';

@Component({
	selector: 'app-search-modal',
	templateUrl: './search-modal.component.html',
	styleUrls: ['./search-modal.component.scss'],
})
export class SearchModalComponent implements OnInit, OnDestroy {
	@Input() restaurant!: MenuDTO;
	@ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

	searchString = '';
	searchResults: Menu | null = null;
	// Lista achatada usada no *ngFor do template. Era um getter que realocava array+Set a
	// cada change detection; agora é um campo recalculado só quando os resultados mudam.
	flattenedResults: MenuProduct[] = [];
	private lastFilteredSearchString: string | null = null;
	private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
	private routerSub!: Subscription;

	constructor(
		private modalCtrl: ModalController,
		private restaurantService: RestaurantService,
		private router: Router,
	) {}

	ngOnInit() {
		this.routerSub = this.router.events.pipe(
			filter(e => e instanceof NavigationStart),
		).subscribe(() => this.modalCtrl.dismiss());
	}

	ngOnDestroy() {
		this.routerSub?.unsubscribe();
	}

	ionViewDidEnter() {
		setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 150);
	}

	get hasSearchTerm(): boolean {
		return !!(this.searchString?.trim());
	}

	get searchResultsEmpty(): boolean {
		const r = this.searchResults;
		if (!r) return false;
		return !r.featuredItems?.length &&
			!r.combos?.length &&
			!r.pizzas?.sizes?.length &&
			!r.general?.some((cat: any) => cat.products?.length);
	}

	private computeFlattenedResults(): MenuProduct[] {
		const results: MenuProduct[] = [];
		const seen = new Set<number>();
		const menu = this.searchResults;
		if (!menu) return results;

		const addUnique = (items: MenuProduct[]) => {
			for (const item of items) {
				if (!seen.has(item.id)) {
					seen.add(item.id);
					results.push(item);
				}
			}
		};

		if (menu.featuredItems?.length) addUnique(menu.featuredItems as unknown as MenuProduct[]);
		if (menu.combos?.length) addUnique(menu.combos as unknown as MenuProduct[]);
		if (menu.pizzas?.sizes?.length) addUnique(menu.pizzas.sizes as unknown as MenuProduct[]);
		if (menu.general?.length) {
			for (const cat of menu.general) {
				if (cat.products?.length) addUnique(cat.products as unknown as MenuProduct[]);
			}
		}
		return results;
	}

	onInput(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		this.searchString = value;
		if (this.searchDebounceTimer !== undefined) clearTimeout(this.searchDebounceTimer);
		if (!value.trim()) {
			this.searchResults = null;
			this.flattenedResults = [];
			this.lastFilteredSearchString = '';
			return;
		}
		this.searchDebounceTimer = setTimeout(() => this.filter(value), 300);
	}

	private filter(str: string) {
		const trimmed = str.trim();
		if (trimmed === this.lastFilteredSearchString) return;
		this.lastFilteredSearchString = trimmed;
		this.searchResults = this.restaurantService.filterMenu(str, this.restaurant.menu);
		this.flattenedResults = this.computeFlattenedResults();
	}

	clearSearch() {
		this.searchString = '';
		this.searchResults = null;
		this.flattenedResults = [];
		this.lastFilteredSearchString = '';
		setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 50);
	}

	trackById(_index: number, product: MenuProduct) {
		return product ? product.id : _index;
	}

	dismiss() {
		this.modalCtrl.dismiss();
	}
}
